import React, { useRef, useState } from 'react';
import { animated, easings, useSpring, useSpringRef } from '@react-spring/web';
import { useClickAway, useMount, useUpdateEffect } from 'react-use';
import { ENTER_ANIMATION_CONFIG } from './panel';
import classNames from 'classnames';
import { Nullable } from 'src/types/nullable.type';
import sample from 'lodash/sample';
import { useCurrentOrganization } from 'src/store/organization';
import * as Yup from 'yup';

export const QuickStartJourneyStatusParams = {
  steps: [
    {
      label: 'Thinking about a title',
      duration: 0.02,
    },
    {
      label: 'Writing your content',
      duration: 0.45,
    },
    {
      label: 'Adding some artwork',
      duration: 0.43,
    },
    { label: 'Wrapping up', duration: 0.1 },
  ],
  duration: 1000 * 60 * 3.5,
};

const ANYTHING_SAMPLES = [
  'the space exploration',
  'the history of the kit homes',
  'the beauty of anime',
  'the history of the Megaplex',
  'the history of the medieval art',
  'the camping trip in Yosemite park',
  'the woodworking',
  'the 90s music',
  'the history of speakeasy bars',
];

let prev = '';
function randomCycle(): string {
  const rand = sample(ANYTHING_SAMPLES);
  if (!rand) return '';

  if (prev === rand) return randomCycle();
  prev = rand;
  return rand;
}

export const QuickStartValidationSchema = Yup.object({
  aiTopic: Yup.string().required('Required'),
});

const ExamplePrompts = [
  {
    label: 'Create presentation about anything',
    prompt: () => `Create a presentation about ${randomCycle()}`,
  },
  {
    label: 'Pitch a product',
    prompt: (userOrg: string) =>
      `You are sales professional at ${userOrg}. Create a pitch for your product in less then 6 slides`,
  },
  {
    label: 'Pitch a product to another company',
    prompt: (userOrg: string) =>
      `You are sales professional at ${userOrg}. Create a pitch why sales team at grammarly.com should use your product`,
  },
  {
    label: 'Create a pitch deck',
    prompt: () => `Create a pitch deck`,
  },
];

export interface CreateJourneyPanelContentProps {
  onSubmit: (values: Record<string, any>) => void;
  onChange: (field: string, value: any) => void;
}

export const CreateJourneyQuickStart = ({ onChange, onSubmit }: CreateJourneyPanelContentProps) => {
  const inputElementRef = useRef<Nullable<HTMLTextAreaElement>>(null);
  const currentOrganization = useCurrentOrganization((state) => state.currentOrganization);

  const examplesContainerRef = useRef<HTMLDivElement>(null);
  const [showExamples, setShowExamples] = useState(false);

  const examplesAnimationApi = useSpringRef();
  const [examplesAnimationStyle] = useSpring(() => ({
    ref: examplesAnimationApi,
    from: { opacity: 0 },
    to: { opacity: 1 },
  }));

  const onCloseExamples = () => {
    setShowExamples(false);
  };

  useClickAway(examplesContainerRef, onCloseExamples);

  useMount(() => {
    onChange('aiTopic', '');
  });

  useUpdateEffect(() => {
    if (showExamples) {
      examplesAnimationApi.start({ opacity: 1, config: ENTER_ANIMATION_CONFIG });
    } else {
      examplesAnimationApi.start({ opacity: 0, config: { duration: 200, easing: easings.easeOutCubic } });
    }
  }, [showExamples]);

  const onSelectExample = (prompt: (userOrg: string) => string) => {
    const generatedPrompt = prompt(currentOrganization.url.replace(/http(s)?:\/\/(www\.)?/i, ''));
    if (inputElementRef.current) {
      inputElementRef.current.value = generatedPrompt;
      onChange('aiTopic', generatedPrompt);
      onCloseExamples();
    }
  };

  return (
    <div className='flex flex-row gap-4'>
      <div className='flex flex-col gap-6 shrink-0 w-full'>
        <p className='text-neue-journey-large'>
          Describe your Journey and have a draft written for you. Use domains, like salesforce.com, when referencing
          companies.
        </p>
        <div className='relative h-[104px]'>
          <textarea
            ref={inputElementRef}
            autoFocus
            className='resize-none neue-journey-text-input h-[inherit] light w-full'
            placeholder='Create presentation about anything'
            name='aiTopic'
            onChange={(e) => {
              const { value } = e.currentTarget as HTMLTextAreaElement;
              onChange('aiTopic', value);
            }}
            onKeyDown={(e) => {
              if (!e.shiftKey && e.key === 'Enter') {
                e.preventDefault();
                onSubmit({ aiTopic: inputElementRef.current?.value || '' });
                return false;
              }
            }}
          />
          <div className='absolute bottom-2 right-2'>
            <div
              onClick={() => {
                setShowExamples(true);
              }}
              className='rounded cursor-pointer bg-neue-journey-bg hover:bg-neue-journey-bg-90 transition-colors px-2 py-1 text-neue-journey-small text-neue-journey-fg'
            >
              Help
            </div>
            <div
              className={classNames('absolute left-full -bottom-2.5', {
                'pointer-events-none': !showExamples,
                'pointer-events-auto': showExamples,
              })}
            ></div>
          </div>
        </div>
      </div>
      <animated.div
        ref={examplesContainerRef}
        className='flex gap-4 w-[257px] h-[172px] shrink-0'
        style={examplesAnimationStyle}
      >
        <div className='w-px h-full mx-1 bg-neue-journey-fg-10'>&nbsp;</div>
        <div className='flex flex-col gap-8 text-neue-journey-fg'>
          <h3 className='text-neue-journey-large'>Pick your goal and have an example prompt written</h3>
          <div className='flex flex-col gap-1'>
            {ExamplePrompts.map(({ label, prompt }) => (
              <div
                key={label}
                className='text-inherit opacity-100 hover:opacity-60 transition-opacity cursor-pointer text-neue-journey-small'
                onClick={() => {
                  onSelectExample(prompt);
                }}
              >
                {label}
              </div>
            ))}
          </div>
        </div>
      </animated.div>
    </div>
  );
};
